import { type VariantProps, cva } from "class-variance-authority"
import { Slot } from "radix-ui"
import * as React from "react"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
        "destructive-outline":
          "border-destructive text-destructive [a&]:hover:bg-destructive [a&]:hover:text-destructive-foreground",
        success: "border-positive/30 bg-positive/10 text-positive",
        male: "border-gender-m-border bg-gender-m-bg text-gender-m",
        female: "border-gender-f-border bg-gender-f-bg text-gender-f",
        // AutoLeads CTA family, see the note in components/ui/button.tsx.
        cta: "bg-primary-cta text-primary-cta-foreground",
        muted: "bg-transparent text-muted-foreground",
        "destructive-soft":
          "border-destructive/20 bg-destructive/10 text-destructive",
        "success-soft": "border-success/20 bg-success/10 text-success",
        "outline-dashed": "border-border border-dashed text-foreground",
      },
      // Badges are pills by default; a few rows want the card's corner.
      shape: {
        default: "",
        lg: "rounded-lg",
      },
      // Categorical tones, picked by hash so a family of niches keeps one
      // colour across the table. The strings live here so they stay static.
      chart: {
        1: "border-chart-1/30 bg-chart-1/10 text-chart-1",
        2: "border-chart-2/30 bg-chart-2/10 text-chart-2",
        3: "border-chart-3/30 bg-chart-3/10 text-chart-3",
        4: "border-chart-4/30 bg-chart-4/10 text-chart-4",
        5: "border-chart-5/30 bg-chart-5/10 text-chart-5",
        6: "border-chart-6/30 bg-chart-6/10 text-chart-6",
        neutral: "border-border bg-muted text-muted-foreground",
      },
      // A badge that is itself a control (a status pill opening its picker):
      // the variant keeps its colours, the press and open states darken it.
      interactive: {
        true: "cursor-pointer outline-none hover:brightness-95 disabled:cursor-default data-[state=open]:brightness-95",
        false: "",
      },
      // Text colour on top of a variant, for quiet metadata badges.
      tone: {
        default: "",
        muted: "text-muted-foreground",
        destructive: "text-destructive",
        primary: "text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
      tone: "default",
      shape: "default",
      interactive: false,
    },
  },
)

function Badge({
  className,
  variant = "default",
  tone,
  chart,
  shape,
  interactive,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(
        badgeVariants({ variant, tone, chart, shape, interactive }),
        className,
      )}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
